import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  CalendarPlus,
  CalendarRange,
  CalendarX2,
  FilePlus2,
  Info,
  Lock,
  Pencil,
  Plus,
  RefreshCw,
  Scissors,
  Trash2,
} from 'lucide-react';
import { BoutonPrincipal, BoutonSecondaire, Pastille } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { detailsRefus } from '../../lib/erreurs-api';
import { usePreparerFactureEcheance } from '../../lib/useFactures';
import { CertificatDeLEcheance } from './CertificatDeLEcheance';
import { AvancementFacturation } from './AvancementFacturation';
import {
  useArreterEcheancierALaRupture,
  useEnregistrerEcheancier,
  useProposerEcheancier,
  type ArretRupture,
  type BlocFinancement,
  type Echeance,
  type Echeancier,
  type FinancementDossier,
} from '../../lib/useFinancements';

type Referentiel = FinancementDossier['referentielEcheancier'];

const CLASSE_CHAMP =
  'min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

const CLASSE_ICONE =
  'inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg text-codialis-gray transition hover:bg-gray-50 hover:text-codialis-navy disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent';

function jourFrancais(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

function dateHeure(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

function messageRefus(erreur: unknown, defaut: string): string {
  const detail = detailsRefus(erreur)[0];
  if (detail) return detail.message;
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/**
 * Jours civils `AAAA-MM-JJ`, comptés sans instant : un `Date` local décalerait
 * d'un jour à La Réunion. Ils ne servent qu'à afficher le début de chaque
 * échéance pendant la saisie ; l'API refait le calendrier et fait foi.
 */
function versJour(iso: string): number {
  const [annee = 0, mois = 1, jour = 1] = iso.split('-').map(Number);
  return Date.UTC(annee, mois - 1, jour) / 86_400_000;
}

function depuisJour(jour: number): string {
  return new Date(jour * 86_400_000).toISOString().slice(0, 10);
}

const lendemain = (iso: string): string => depuisJour(versJour(iso) + 1);

function libellePeriodicite(mois: number): string {
  return mois === 1 ? 'Tous les mois' : `Tous les ${mois} mois`;
}

/**
 * Échéances de facturation d'un contrat d'apprentissage (TF-409). Le
 * calendrier part du début du contrat, au mois, avec une périodicité propre au
 * contrat ; il se génère, puis se corrige à la main. Ce qu'une facture couvre
 * est figé. Chaque échéance dit ce qu'elle facture — le coût pédagogique cumulé
 * jusqu'à sa fin, moins le déjà facturé (TF-410) —, et la prochaine porte
 * « Préparer la facture ». À partir de la deuxième, chacune porte aussi son
 * certificat de réalisation (TF-412). Une rupture arrête le calendrier à sa
 * date : l'échéance qui la contient y est ramenée, les suivantes sont annulées
 * et restent en fin de liste, hors calendrier (TF-414). En tête, l'avancement
 * de la facturation : dû, facturé, reste et prochaine étape (TF-415). État,
 * durées, montants et refus viennent de l'API.
 */
export function EcheancierDuContrat({
  bloc,
  referentiel,
}: {
  bloc: BlocFinancement;
  referentiel: Referentiel;
}): JSX.Element {
  const [generation, setGeneration] = useState(false);
  const [modification, setModification] = useState(false);
  const { echeancier } = bloc;
  const { echeances, situation } = echeancier;
  // Les échéances annulées par la rupture ne font plus le calendrier : ni comptées, ni saisies.
  const actives = echeances.filter((echeance) => !echeance.annulation);
  const employeur = bloc.contrat.employeur.denomination;
  const bloque = echeancier.refus !== null;

  return (
    <section className="rounded-2xl border border-gray-100" aria-label={`Échéances de facturation du contrat chez ${employeur}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5 sm:px-4">
        <h5 className="flex min-w-0 items-center gap-2 text-sm font-bold text-codialis-navy">
          <CalendarRange className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
          Échéances de facturation
          {actives.length > 0 && (
            <span className="text-xs font-semibold text-codialis-gray">({actives.length})</span>
          )}
        </h5>
        <Pastille texte={situation.libelle} ton={situation.ton} />
      </div>

      <div className="space-y-3 p-3 sm:p-4">
        <AvancementFacturation avancement={echeancier.avancement} />
        {echeancier.refus && (
          <p className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{echeancier.refus}</p>
        )}
        {echeancier.bornes.detail && (
          <p className="flex gap-1.5 text-xs text-codialis-gray">
            <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="min-w-0">{echeancier.bornes.detail}</span>
          </p>
        )}
        {echeancier.rupture && !echeancier.rupture.arrete && (
          <ArretDuCalendrier contratId={bloc.contrat.id} rupture={echeancier.rupture} />
        )}
        {/* Calendrier pas encore arrêté à la rupture : le panneau dit quoi faire, mieux que « le régénérer ». */}
        {situation.detail && !(echeancier.rupture && !echeancier.rupture.arrete) && (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">{situation.detail}</p>
        )}

        {echeances.length === 0 ? (
          !bloque && (
            <p className="text-sm text-codialis-gray">
              Aucune échéance. Le calendrier part du début du contrat, le{' '}
              {jourFrancais(echeancier.bornes.debut)}, et le couvre jusqu’au {jourFrancais(echeancier.bornes.fin)}.
            </p>
          )
        ) : (
          <>
            <p className="text-sm text-codialis-navy">
              <span className="font-semibold">{echeancier.periodicite.texte}</span>
              <span className="text-codialis-gray">
                {' '}
                · du {jourFrancais(echeancier.bornes.debut)} au {jourFrancais(echeancier.bornes.fin)}
              </span>
            </p>
            <ol className="divide-y divide-gray-100 rounded-xl border border-gray-100 bg-white">
              {echeances.map((echeance) => (
                <li
                  key={echeance.id}
                  className={`flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5 px-3 py-2.5 sm:px-4 ${
                    echeance.annulation ? 'bg-gray-50' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <p
                      className={`text-sm font-semibold ${echeance.annulation ? 'text-codialis-gray' : 'text-codialis-navy'}`}
                    >
                      <span className="mr-2 text-xs font-bold text-codialis-gray">N° {echeance.rang}</span>
                      <span className={`tabular-nums ${echeance.annulation ? 'line-through' : ''}`}>
                        {jourFrancais(echeance.dateDebut)} → {jourFrancais(echeance.dateFin)}
                      </span>
                    </p>
                    <p className="text-[11px] text-codialis-gray">
                      {echeance.duree}
                      {echeance.situation.detail ? ` · ${echeance.situation.detail}` : ''}
                    </p>
                    {echeance.annulation && (
                      <p className="text-[11px] text-codialis-gray">
                        Annulée le {dateHeure(echeance.annulation.le)}
                        {echeance.annulation.par ? ` par ${echeance.annulation.par}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {echeance.verrou && (
                      <span title={echeance.verrou} className="inline-flex text-codialis-gray">
                        <Lock className="h-3.5 w-3.5" aria-hidden />
                        <span className="sr-only">{echeance.verrou}</span>
                      </span>
                    )}
                    <Pastille texte={echeance.situation.libelle} ton={echeance.situation.ton} />
                  </div>
                  {echeance.montant && <MontantDeLEcheance echeance={echeance} />}
                  <CertificatDeLEcheance echeance={echeance} />
                </li>
              ))}
            </ol>
          </>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="min-w-0 text-[11px] text-codialis-gray">
            {echeancier.modification
              ? `Modifié le ${dateHeure(echeancier.modification.le)}${
                  echeancier.modification.par ? ` par ${echeancier.modification.par}` : ''
                }.`
              : 'Une échéance est due au financeur à son dernier jour.'}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {actives.length > 0 && (
              <BoutonSecondaire onClick={() => setModification(true)} disabled={bloque}>
                <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
                Modifier
              </BoutonSecondaire>
            )}
            <BoutonSecondaire onClick={() => setGeneration(true)} disabled={bloque}>
              {actives.length > 0 ? (
                <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden />
              ) : (
                <CalendarPlus className="mr-1.5 h-4 w-4" aria-hidden />
              )}
              {actives.length > 0 ? 'Régénérer' : 'Générer le calendrier'}
            </BoutonSecondaire>
          </div>
        </div>
      </div>

      {generation && (
        <ModaleGeneration
          contratId={bloc.contrat.id}
          employeur={employeur}
          echeancier={echeancier}
          referentiel={referentiel}
          onFermer={() => setGeneration(false)}
        />
      )}
      {modification && (
        <ModaleModification
          contratId={bloc.contrat.id}
          employeur={employeur}
          echeancier={echeancier}
          referentiel={referentiel}
          onFermer={() => setModification(false)}
        />
      )}
    </section>
  );
}

/**
 * La rupture du contrat, datée, n'a pas encore arrêté le calendrier (TF-414) :
 * ce que l'arrêt ferait, ce qui l'en empêche — un avoir à établir, un
 * brouillon à supprimer —, et le geste qui le rejoue une fois l'obstacle levé.
 */
function ArretDuCalendrier({ contratId, rupture }: { contratId: string; rupture: ArretRupture }): JSX.Element {
  const toast = useToast();
  const arreter = useArreterEcheancierALaRupture();
  const [erreur, setErreur] = useState<string | null>(null);
  const possible = rupture.aFaire.length > 0;

  const lancer = (): void => {
    setErreur(null);
    arreter.mutate(contratId, {
      onSuccess: ({ arret }) =>
        toast(
          arret.obstacles.length > 0
            ? 'Calendrier arrêté en partie : ce qui bloque encore reste indiqué.'
            : 'Calendrier arrêté à la rupture.',
        ),
      onError: (motif) => setErreur(messageRefus(motif, 'Le calendrier n’a pas pu être arrêté.')),
    });
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5" role="group" aria-label="Arrêt du calendrier à la rupture">
      <p className="flex gap-1.5 text-xs font-bold text-amber-900">
        <CalendarX2 className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="min-w-0">
          Contrat rompu le {jourFrancais(rupture.date)} : le calendrier n’est pas encore arrêté à la rupture.
        </span>
      </p>
      {possible && (
        <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-[11px] text-amber-900">
          {rupture.aFaire.map((phrase) => (
            <li key={phrase} className="break-words">
              {phrase}
            </li>
          ))}
        </ul>
      )}
      {rupture.obstacles.length > 0 && (
        <ul className="mt-1.5 space-y-1 text-[11px] font-semibold text-red-700">
          {rupture.obstacles.map((obstacle) => (
            <li key={obstacle} className="break-words">
              {obstacle}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
        <BoutonPrincipal onClick={lancer} disabled={!possible || arreter.isPending}>
          <CalendarX2 className="mr-1.5 h-4 w-4" aria-hidden />
          {arreter.isPending ? 'Arrêt…' : 'Arrêter le calendrier à la rupture'}
        </BoutonPrincipal>
      </div>
      {erreur && (
        <p role="alert" className="mt-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold text-red-700">
          {erreur}
        </p>
      )}
    </div>
  );
}

/**
 * Ce que l'échéance facture, et son calcul ; sur la prochaine à facturer, le
 * geste qui prépare sa facture en brouillon, ou ce qui l'en empêche encore. La
 * dernière est le solde du parcours, en jours ouvrés (TF-415) ; un écart à
 * corriger — trop facturé, facturée au mois — y est signalé.
 */
function MontantDeLEcheance({ echeance }: { echeance: Echeance }): JSX.Element | null {
  const toast = useToast();
  const preparer = usePreparerFactureEcheance();
  const [erreur, setErreur] = useState<string | null>(null);
  const { montant, preparation } = echeance;
  if (!montant) return null;
  const refus = preparation?.refus ?? null;

  const lancer = (): void => {
    setErreur(null);
    preparer.mutate(echeance.id, {
      onSuccess: () =>
        toast(
          montant.solde
            ? 'Facture de solde préparée : vérifiez-la avant de l’émettre.'
            : `Facture de l’échéance n° ${echeance.rang} préparée : vérifiez-la avant de l’émettre.`,
        ),
      onError: (motif) => setErreur(messageRefus(motif, 'La facture n’a pas pu être préparée.')),
    });
  };

  return (
    <div
      className={`w-full min-w-0 rounded-lg px-3 py-2 ${
        montant.alerte ? 'border border-amber-200 bg-amber-50' : 'bg-codialis-sand'
      }`}
    >
      <p className="flex flex-wrap items-baseline justify-between gap-x-3 text-xs">
        <span className={`font-semibold ${montant.alerte ? 'text-amber-800' : 'text-codialis-gray'}`}>
          {montant.libelle}
        </span>
        {montant.texte && (
          <span
            className={`font-display text-sm font-extrabold tabular-nums ${
              montant.alerte ? 'text-amber-800' : 'text-codialis-navy'
            }`}
          >
            {montant.texte}
          </span>
        )}
      </p>
      {montant.detail && (
        <p className={`mt-0.5 break-words text-[11px] ${montant.alerte ? 'text-amber-800' : 'text-codialis-gray'}`}>
          {montant.detail}
        </p>
      )}
      {preparation && (
        // Au téléphone, le refus au-dessus du bouton : à côté, il tiendrait dans une colonne de trois mots.
        <div className="mt-2 flex flex-col items-end gap-1.5 sm:flex-row sm:items-center sm:justify-end sm:gap-x-3">
          {refus && <p className="w-full min-w-0 text-[11px] text-codialis-gray sm:w-auto sm:flex-1">{refus}</p>}
          <BoutonPrincipal onClick={lancer} disabled={refus !== null || preparer.isPending}>
            <FilePlus2 className="mr-1.5 h-4 w-4" aria-hidden />
            {preparer.isPending
              ? 'Préparation…'
              : montant.solde
                ? 'Préparer la facture de solde'
                : 'Préparer la facture'}
          </BoutonPrincipal>
        </div>
      )}
      {erreur && (
        <p role="alert" className="mt-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold text-red-700">
          {erreur}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Générer
// ---------------------------------------------------------------------------

const LISTE = 'LISTE';
/** Saisie libre (TF-466) : aucune périodicité, chaque échéance se saisit date à date. */
const LIBRE = 'LIBRE';

/** « 1, 3, 3, 6 » → [1, 3, 3, 6] ; `null` si l'écriture n'est pas une liste de mois entiers. */
function dureesSaisies(texte: string): number[] | null {
  const morceaux = texte.split(/[\s,;]+/).filter(Boolean);
  if (morceaux.length === 0 || morceaux.some((morceau) => !/^\d+$/.test(morceau))) return null;
  return morceaux.map(Number);
}

function ModaleGeneration({
  contratId,
  employeur,
  echeancier,
  referentiel,
  onFermer,
}: {
  contratId: string;
  employeur: string;
  echeancier: Echeancier;
  referentiel: Referentiel;
  onFermer: () => void;
}): JSX.Element {
  const proposer = useProposerEcheancier();
  const actuelle = echeancier.periodicite.mois;
  const [choix, setChoix] = useState<string>(() =>
    String(
      actuelle !== null && referentiel.dureesProposees.includes(actuelle) ? actuelle : referentiel.dureeParDefaut,
    ),
  );
  const [liste, setListe] = useState('');
  const [lignes, setLignes] = useState<LigneInitiale[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  // Une réponse arrivée après un autre choix est ignorée ; chaque proposition reçue remonte l'éditeur.
  const demande = useRef(0);
  const [version, setVersion] = useState(0);
  const figees = echeancier.echeances.filter((echeance) => echeance.verrou && !echeance.annulation).length;
  const annulees = echeancier.echeances.filter((echeance) => echeance.annulation).length;

  const proposerPour = (durees: number[] | null): void => {
    const numero = (demande.current += 1);
    setErreur(null);
    proposer.mutate(
      { contratId, durees },
      {
        onSuccess: (proposition) => {
          if (numero !== demande.current) return;
          const gardees: LigneInitiale[] = proposition.echeances.map((echeance) => ({
            debut: echeance.dateDebut,
            fin: echeance.dateFin,
            verrou: echeance.verrou,
          }));
          const derniere = gardees.at(-1);
          // En saisie libre, une première ligne à compléter : au début du contrat, ou au lendemain de ce qui est gardé.
          setLignes(
            durees
              ? gardees
              : [...gardees, { debut: derniere ? lendemain(derniere.fin) : echeancier.bornes.debut, fin: '', verrou: null }],
          );
          setVersion((courante) => courante + 1);
        },
        onError: (refus) => {
          if (numero === demande.current) setErreur(messageRefus(refus, 'Le calendrier n’a pas pu être calculé.'));
        },
      },
    );
  };

  const changerChoix = (valeur: string): void => {
    demande.current += 1;
    setChoix(valeur);
    setLignes(null);
    setErreur(null);
    if (valeur === LIBRE) proposerPour(null);
  };

  const calculer = (): void => {
    if (choix === LIBRE) return;
    const durees = choix === LISTE ? dureesSaisies(liste) : [Number(choix)];
    if (!durees) {
      setErreur('Écrire des mois entiers, séparés par des virgules : 1, 3, 3, 6.');
      return;
    }
    proposerPour(durees);
  };

  return (
    <Modale
      ouverte
      titre={echeancier.echeances.some((echeance) => !echeance.annulation) ? 'Régénérer le calendrier' : 'Générer le calendrier'}
      sousTitre={`Contrat chez ${employeur} · du ${jourFrancais(echeancier.bornes.debut)} au ${jourFrancais(echeancier.bornes.fin)}`}
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-5 p-4 sm:p-6">
        <form
          onSubmit={(evenement) => {
            evenement.preventDefault();
            calculer();
          }}
          className="space-y-3"
          aria-label="Périodicité des échéances"
        >
          <fieldset className="min-w-0">
            <legend className="mb-2 text-xs font-semibold text-codialis-navy">Périodicité</legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {referentiel.dureesProposees.map((mois) => (
                <OptionPeriodicite
                  key={mois}
                  nom={`periodicite-${contratId}`}
                  valeur={String(mois)}
                  choisie={choix === String(mois)}
                  onChoisir={changerChoix}
                  libelle={libellePeriodicite(mois)}
                />
              ))}
              <OptionPeriodicite
                nom={`periodicite-${contratId}`}
                valeur={LISTE}
                choisie={choix === LISTE}
                onChoisir={changerChoix}
                libelle="Durées successives"
              />
              <OptionPeriodicite
                nom={`periodicite-${contratId}`}
                valeur={LIBRE}
                choisie={choix === LIBRE}
                onChoisir={changerChoix}
                libelle="Saisie libre"
              />
            </div>
          </fieldset>
          {choix === LISTE && (
            <div className="min-w-0">
              <label htmlFor={`durees-${contratId}`} className="mb-1 block text-[11px] font-semibold text-codialis-gray">
                Durées, en mois
              </label>
              <input
                id={`durees-${contratId}`}
                value={liste}
                onChange={(e) => {
                  setListe(e.target.value);
                  setLignes(null);
                  setErreur(null);
                }}
                placeholder="1, 3, 3, 6"
                inputMode="numeric"
                autoComplete="off"
                className={`${CLASSE_CHAMP} tabular-nums`}
              />
            </div>
          )}
          <p className="flex gap-1.5 text-[11px] text-codialis-gray">
            <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="min-w-0">
              {choix === LIBRE
                ? 'Chaque échéance se saisit de telle date à telle date : la première commence avec le contrat, chacune le lendemain de la précédente, la dernière finit avec le contrat.'
                : 'Le calendrier part du début du contrat. La dernière durée se répète jusqu’à la fin : « 1, 3 » donne un premier mois, puis des trimestres. Une dernière période plus courte est gardée telle quelle. Les échéances restent modifiables ensuite.'}
              {figees > 0 &&
                ` ${figees === 1 ? 'L’échéance qu’une facture couvre est gardée' : `Les ${figees} échéances figées sont gardées`} ; les autres sont remplacées.`}
              {annulees > 0 && ' Les échéances annulées par la rupture restent telles quelles, hors calendrier.'}
            </span>
          </p>
          {/* En saisie libre, l'éditeur s'ouvre de lui-même : rien à calculer. */}
          {(choix !== LIBRE || !lignes) && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {!lignes && <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>}
              {choix !== LIBRE && (
                <BoutonSecondaire onClick={calculer} disabled={proposer.isPending}>
                  <CalendarRange className="mr-1.5 h-4 w-4" aria-hidden />
                  {proposer.isPending ? 'Calcul…' : 'Calculer le calendrier'}
                </BoutonSecondaire>
              )}
            </div>
          )}
        </form>

        {erreur && !lignes && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}

        {lignes && (
          <EditeurEcheances
            key={version}
            contratId={contratId}
            echeancier={echeancier}
            referentiel={referentiel}
            initiales={lignes}
            libelleEnregistrer="Enregistrer le calendrier"
            onFermer={onFermer}
          />
        )}
      </div>
    </Modale>
  );
}

function OptionPeriodicite({
  nom,
  valeur,
  choisie,
  onChoisir,
  libelle,
}: {
  nom: string;
  valeur: string;
  choisie: boolean;
  onChoisir: (valeur: string) => void;
  libelle: string;
}): JSX.Element {
  return (
    <label
      className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
        choisie
          ? 'border-codialis-sky bg-codialis-sky/5 font-semibold text-codialis-navy'
          : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
      }`}
    >
      <input
        type="radio"
        name={nom}
        value={valeur}
        checked={choisie}
        onChange={() => onChoisir(valeur)}
        className="h-4 w-4 shrink-0 accent-codialis-sky"
      />
      <span className="min-w-0">{libelle}</span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Modifier
// ---------------------------------------------------------------------------

function ModaleModification({
  contratId,
  employeur,
  echeancier,
  referentiel,
  onFermer,
}: {
  contratId: string;
  employeur: string;
  echeancier: Echeancier;
  referentiel: Referentiel;
  onFermer: () => void;
}): JSX.Element {
  // Une échéance annulée par la rupture ne se saisit plus : elle reste telle quelle.
  const initiales = echeancier.echeances
    .filter((echeance) => !echeance.annulation)
    .map((echeance) => ({ debut: echeance.dateDebut, fin: echeance.dateFin, verrou: echeance.verrou }));
  return (
    <Modale
      ouverte
      titre="Modifier les échéances"
      sousTitre={`Contrat chez ${employeur} · du ${jourFrancais(echeancier.bornes.debut)} au ${jourFrancais(echeancier.bornes.fin)}`}
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="p-4 sm:p-6">
        <EditeurEcheances
          contratId={contratId}
          echeancier={echeancier}
          referentiel={referentiel}
          initiales={initiales}
          libelleEnregistrer="Enregistrer"
          onFermer={onFermer}
        />
      </div>
    </Modale>
  );
}

/** Une échéance telle que l'éditeur la reçoit : ses dates, `''` tant qu'elles restent à saisir. */
interface LigneInitiale {
  debut: string;
  fin: string;
  /** Ce qui la fige ; `null` : modifiable. */
  verrou: string | null;
}

/** Une échéance en cours de saisie ; `cle` la suit quand des lignes s'ajoutent ou se retirent. */
interface LigneSaisie extends LigneInitiale {
  cle: number;
}

/** Jours couverts, bornes comprises ; `null` tant que la période n'est pas lisible. */
function joursCouverts(debut: string, fin: string): number | null {
  if (debut === '' || fin === '' || fin < debut) return null;
  return versJour(fin) - versJour(debut) + 1;
}

const veille = (iso: string): string => depuisJour(versJour(iso) - 1);

/**
 * Le calendrier se saisit **date à date** (TF-466) : chaque échéance a son
 * début et sa fin. Deux échéances qui se suivent restent collées quand l'une
 * bouge — la fin de l'une, le début de la suivante —, et une échéance retirée
 * laisse ses dates à sa voisine. Une échéance figée ne bouge pas, ni la borne
 * de ses voisines qui la touche. Le nombre de jours de chaque ligne suit la
 * saisie ; chevauchements, trous et couverture du contrat sont jugés par l'API
 * à l'enregistrement.
 */
function EditeurEcheances({
  contratId,
  echeancier,
  referentiel,
  initiales,
  libelleEnregistrer,
  onFermer,
}: {
  contratId: string;
  echeancier: Echeancier;
  referentiel: Referentiel;
  initiales: LigneInitiale[];
  libelleEnregistrer: string;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const enregistrer = useEnregistrerEcheancier();
  const { debut: debutContrat, fin: finContrat } = echeancier.bornes;
  const prochaineCle = useRef(initiales.length);
  // Le calendrier commence et finit avec le contrat — ou sa rupture —, sauf là où une échéance est figée.
  const [lignes, setLignes] = useState<LigneSaisie[]>(() =>
    initiales.map((ligne, index) => {
      const debut = index === 0 && !ligne.verrou && ligne.debut !== '' ? debutContrat : ligne.debut;
      const fin = index === initiales.length - 1 && !ligne.verrou && ligne.fin !== '' ? finContrat : ligne.fin;
      return { ...ligne, debut, fin, cle: index };
    }),
  );
  const [aFocaliser, setAFocaliser] = useState<number | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (aFocaliser !== null) document.getElementById(`echeance-${contratId}-${aFocaliser}-fin`)?.focus();
  }, [aFocaliser, contratId]);

  const modifier = (suivantes: LigneSaisie[]): void => {
    setLignes(suivantes);
    setErreur(null);
  };

  const libre = (index: number): boolean => {
    const ligne = lignes[index];
    return ligne !== undefined && !ligne.verrou;
  };
  /** Le début se saisit si l'échéance est libre et que la précédente l'est aussi : il touche sa fin. */
  const debutModifiable = (index: number): boolean => libre(index) && (index === 0 || libre(index - 1));
  /** La fin se saisit si l'échéance est libre et que la suivante l'est aussi. */
  const finModifiable = (index: number): boolean =>
    libre(index) && (index === lignes.length - 1 || libre(index + 1));

  // Une précédente qui finissait la veille suit le nouveau début.
  const changerDebut = (index: number, debut: string): void => {
    const ancien = lignes[index]?.debut ?? '';
    const precedente = lignes[index - 1];
    const collee = ancien !== '' && debut !== '' && precedente !== undefined && precedente.fin === veille(ancien);
    modifier(
      lignes.map((ligne, rang) =>
        rang === index ? { ...ligne, debut } : rang === index - 1 && collee ? { ...ligne, fin: veille(debut) } : ligne,
      ),
    );
  };

  // Une suivante qui commençait le lendemain suit la nouvelle fin.
  const changerFin = (index: number, fin: string): void => {
    const ancienne = lignes[index]?.fin ?? '';
    const suivante = lignes[index + 1];
    const collee = ancienne !== '' && fin !== '' && suivante !== undefined && suivante.debut === lendemain(ancienne);
    modifier(
      lignes.map((ligne, rang) =>
        rang === index ? { ...ligne, fin } : rang === index + 1 && collee ? { ...ligne, debut: lendemain(fin) } : ligne,
      ),
    );
  };

  const nouvelleCle = (): number => {
    prochaineCle.current += 1;
    return prochaineCle.current;
  };

  const peutAjouter = lignes.length < referentiel.echeancesMaximum;

  /** Une échéance de plus, au lendemain de la dernière : sa fin reste à saisir. */
  const ajouter = (): void => {
    const derniere = lignes.at(-1);
    const debut = derniere ? (derniere.fin === '' ? '' : lendemain(derniere.fin)) : debutContrat;
    const cle = nouvelleCle();
    modifier([...lignes, { cle, debut, fin: '', verrou: null }]);
    setAFocaliser(cle);
  };

  const peutScinder = (index: number): boolean => {
    const ligne = lignes[index];
    return (
      ligne !== undefined &&
      !ligne.verrou &&
      lignes.length < referentiel.echeancesMaximum &&
      (joursCouverts(ligne.debut, ligne.fin) ?? 0) > 1
    );
  };

  const scinder = (index: number): void => {
    const ligne = lignes[index];
    if (!ligne) return;
    const milieu = depuisJour(Math.floor((versJour(ligne.debut) + versJour(ligne.fin)) / 2));
    modifier([
      ...lignes.slice(0, index),
      { ...ligne, fin: milieu },
      { cle: nouvelleCle(), debut: lendemain(milieu), fin: ligne.fin, verrou: null },
      ...lignes.slice(index + 1),
    ]);
  };

  const peutRetirer = (index: number): boolean => lignes.length > 1 && libre(index);

  /**
   * Retirer une échéance : la suivante, si elle lui était collée et libre,
   * reprend son début ; sinon la précédente, aux mêmes conditions, sa fin.
   */
  const retirer = (index: number): void => {
    const ligne = lignes[index];
    if (!ligne) return;
    const suivante = lignes[index + 1];
    const precedente = lignes[index - 1];
    const complete = ligne.debut !== '' && ligne.fin !== '';
    const versSuivante =
      complete && suivante !== undefined && !suivante.verrou && suivante.debut === lendemain(ligne.fin);
    const versPrecedente =
      !versSuivante && complete && precedente !== undefined && !precedente.verrou && precedente.fin === veille(ligne.debut);
    modifier(
      lignes.flatMap((autre, rang) => {
        if (rang === index) return [];
        if (rang === index + 1 && versSuivante) return [{ ...autre, debut: ligne.debut }];
        if (rang === index - 1 && versPrecedente) return [{ ...autre, fin: ligne.fin }];
        return [autre];
      }),
    );
  };

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (lignes.some((ligne) => ligne.debut === '' || ligne.fin === '')) {
      setErreur('Chaque échéance a une date de début et une date de fin.');
      return;
    }
    enregistrer.mutate(
      { contratId, echeances: lignes.map((ligne) => ({ dateDebut: ligne.debut, dateFin: ligne.fin })) },
      {
        onSuccess: () => {
          toast('Calendrier des échéances enregistré.');
          onFermer();
        },
        onError: (refus) => setErreur(messageRefus(refus, 'Le calendrier n’a pas pu être enregistré.')),
      },
    );
  };

  return (
    <form onSubmit={soumettre} className="space-y-4" aria-label="Échéances du calendrier">
      <ol className="space-y-2">
        {lignes.map((ligne, index) => {
          const numero = index + 1;
          const jours = joursCouverts(ligne.debut, ligne.fin);
          return (
            <li
              key={ligne.cle}
              // Au téléphone : numéro et gestes en tête, dates dessous ; plus large, une seule ligne.
              className={`grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 rounded-xl border p-3 sm:grid-cols-[5rem_minmax(0,1fr)_minmax(0,1fr)_auto] ${
                ligne.verrou ? 'border-gray-100 bg-gray-50' : 'border-gray-100 bg-white'
              }`}
            >
              <div className="order-1 min-w-0">
                <p className="flex items-center gap-1.5 text-xs font-bold text-codialis-gray">
                  N° {numero}
                  {ligne.verrou && (
                    <span title={ligne.verrou} className="inline-flex">
                      <Lock className="h-3.5 w-3.5" aria-hidden />
                      <span className="sr-only">{ligne.verrou}</span>
                    </span>
                  )}
                </p>
                <p className="text-[11px] tabular-nums text-codialis-gray">
                  {jours === null ? '—' : jours === 1 ? '1 jour' : `${jours} jours`}
                </p>
              </div>
              <BorneEcheance
                id={`echeance-${contratId}-${ligne.cle}-debut`}
                prefixe="Du"
                nom={`Début de l’échéance n° ${numero}`}
                valeur={ligne.debut}
                modifiable={debutModifiable(index)}
                min={debutContrat}
                max={finContrat}
                onChanger={(valeur) => changerDebut(index, valeur)}
                className="order-3 col-span-2 sm:order-2 sm:col-span-1"
              />
              <BorneEcheance
                id={`echeance-${contratId}-${ligne.cle}-fin`}
                prefixe="au"
                nom={`Fin de l’échéance n° ${numero}`}
                valeur={ligne.fin}
                modifiable={finModifiable(index)}
                min={ligne.debut || debutContrat}
                max={finContrat}
                onChanger={(valeur) => changerFin(index, valeur)}
                className="order-4 col-span-2 sm:order-3 sm:col-span-1"
              />
              <div className="order-2 flex items-center justify-end gap-1 sm:order-4">
                <button
                  type="button"
                  onClick={() => scinder(index)}
                  disabled={!peutScinder(index)}
                  aria-label={`Scinder l’échéance n° ${numero} en deux`}
                  title="Scinder en deux"
                  className={CLASSE_ICONE}
                >
                  <Scissors className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => retirer(index)}
                  disabled={!peutRetirer(index)}
                  aria-label={`Retirer l’échéance n° ${numero}`}
                  title="Retirer : sa voisine reprend ses dates"
                  className={`${CLASSE_ICONE} hover:text-red-700`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </li>
          );
        })}
      </ol>

      <div>
        <BoutonSecondaire onClick={ajouter} disabled={!peutAjouter}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Ajouter une échéance
        </BoutonSecondaire>
      </div>

      <p className="flex gap-1.5 text-[11px] text-codialis-gray">
        <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="min-w-0">
          Chaque échéance commence le lendemain de la précédente : ni chevauchement, ni trou. La première commence
          avec le contrat, la dernière finit avec lui. Les échéances qu’une facture couvre ne changent plus. La
          plateforme contrôle le calendrier à l’enregistrement.
        </span>
      </p>

      {erreur && (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
          {erreur}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <BoutonSecondaire onClick={onFermer} disabled={enregistrer.isPending}>
          Annuler
        </BoutonSecondaire>
        <BoutonPrincipal type="submit" disabled={enregistrer.isPending}>
          {enregistrer.isPending ? 'Enregistrement…' : libelleEnregistrer}
        </BoutonPrincipal>
      </div>
    </form>
  );
}

/** Une borne d'échéance : un champ date quand elle se saisit, sa date sinon. */
function BorneEcheance({
  id,
  prefixe,
  nom,
  valeur,
  modifiable,
  min,
  max,
  onChanger,
  className,
}: {
  id: string;
  prefixe: string;
  nom: string;
  valeur: string;
  modifiable: boolean;
  min: string;
  max: string;
  onChanger: (valeur: string) => void;
  className: string;
}): JSX.Element {
  return (
    <div className={`flex min-w-0 items-center gap-2 ${className}`}>
      <span className="w-5 shrink-0 text-[11px] font-semibold text-codialis-gray" aria-hidden>
        {prefixe}
      </span>
      {modifiable ? (
        <input
          id={id}
          type="date"
          required
          aria-label={nom}
          value={valeur}
          min={min}
          max={max}
          onChange={(e) => onChanger(e.target.value)}
          className={CLASSE_CHAMP}
        />
      ) : (
        <span className="min-w-0 text-sm tabular-nums text-codialis-navy">
          <span className="sr-only">{nom} : </span>
          {valeur === '' ? '—' : jourFrancais(valeur)}
        </span>
      )}
    </div>
  );
}
